import axios from 'axios';
import { logError } from '../Components/ErrorLogService/ErrorLoggingService';

/**
 * Sets up global application-wide network interceptors for both Fetch and Axios
 * to automatically route all 4xx/5xx failures directly into the Firestore 'errors' collection.
 */
export const initializeNetworkInterceptors = () => {
  if (typeof window === 'undefined') return;

  // HEAVY-DUTY RECURSION GUARD:
  // Prevents infinite loops if Firebase itself fails (like App Check or Firestore)
  const shouldIgnore = (url: string) => {
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.includes('googleapis.com') || 
      lowerUrl.includes('firebase') ||
      lowerUrl.includes('securetoken.net') // Google Auth token domain
    );
  };

  // ==========================================
  // 1. AXIOS GLOBAL INTERCEPTOR
  // ==========================================
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const failedUrl = error.config?.url || "Unknown Endpoint";
      const status = error.response?.status || "Network Crash";

      // Ignore Google internal failures to prevent loop
      if (!shouldIgnore(failedUrl)) {
        
        // 💡 INTELLIGENT EXTRACTION: Pull actual function name from the Cloud Endpoint URL
        let functionName = "Global_Axios_Monitor";
        if (failedUrl.includes('.cloudfunctions.net/') || failedUrl.includes('.a.run.app/')) {
          const urlParts = failedUrl.split('/');
          functionName = `🔥 Endpoint FAILED: ${urlParts[urlParts.length - 1]}`;
        }

        logError(
          {
            type: 'AXIOS_FAILURE',
            url: failedUrl,
            status: status,
            method: error.config?.method?.toUpperCase(),
            rawMessage: error.message
          },
          functionName
        ).catch(() => {});
      }

      return Promise.reject(error);
    }
  );

  // ==========================================
  // 2. FETCH API MONKEY PATCH (Universal Safety Net)
  // ==========================================
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      const targetUrl = args[0] ? args[0].toString() : "Unknown Fetch";

      // 🛑 Check status code and enforce safety recursion guard
      if (!response.ok && !shouldIgnore(targetUrl)) {
        
        // 💡 INTELLIGENT EXTRACTION: Pull actual function name from the Cloud Endpoint URL
        let functionName = "Global_Fetch_Monitor";
        if (targetUrl.includes('.cloudfunctions.net/') || targetUrl.includes('.a.run.app/')) {
          const urlParts = targetUrl.split('/');
          functionName = `🔥 Endpoint FAILED: ${urlParts[urlParts.length - 1]}`;
        }

        logError(
          {
            type: 'FETCH_FAILURE',
            url: targetUrl,
            status: response.status,
            statusText: response.statusText,
            timestamp: new Date().toISOString()
          },
          functionName // Passes the smart function name instead of generic string
        ).catch(() => {});
      }

      return response;
    } catch (err: any) {
      const targetUrl = args[0] ? args[0].toString() : "Unknown Fetch";
      
      // Catch real browser crashes only for EXTERNAL targets
      if (!shouldIgnore(targetUrl)) {
        logError(
          {
            type: 'FETCH_CRASH',
            url: targetUrl,
            reason: err.message || 'Connection Refused'
          },
          'Global_Fetch_Monitor'
        ).catch(() => {});
      }
      throw err;
    }
  };

  console.log('✅ Universal API Error Monitoring Active (Armed with Recursion Guard)');
};
